'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuthStore } from '@/lib/auth-store';
import type { Benutzer } from '@/lib/api/auth-types';
import { useStatus } from '@/lib/hooks/use-status';
import { resolveSeitenFeatureFlags, type SeitenFeatureFlags } from '@/lib/feature-flags';
import {
  hatZugriffAufSeite as benutzerHatZugriffAufSeite,
  startseiteFuerBenutzer,
} from '@/lib/page-permissions';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Schützt alle Routen außer /login.
 * Prüft ob der Benutzer angemeldet ist und leitet bei Bedarf um.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { istAngemeldet, benutzer } = useAuthStore();
  const { data: status, isLoading: statusLaedt, error: statusFehler } = useStatus();
  const router = useRouter();
  const pathname = usePathname();
  const [bereit, setBereit] = useState(false);

  useEffect(() => {
    // Login-Seite braucht keinen Guard
    if (pathname === '/login') {
      setBereit(true);
      return;
    }

    if (!istAngemeldet) {
      router.replace('/login');
      return;
    }

    if (!benutzer) {
      router.replace('/login');
      return;
    }

    if (statusLaedt && !status && !statusFehler) {
      return;
    }

    const seitenFeatureFlags = resolveSeitenFeatureFlags(status);
    const erpAdapterTyp = status?.config_zusammenfassung?.adapter?.erp;

    if (!hatZugriffAufSeite(benutzer, pathname, seitenFeatureFlags, erpAdapterTyp)) {
      router.replace(startseiteFuerBenutzer(benutzer, seitenFeatureFlags, erpAdapterTyp));
      return;
    }

    setBereit(true);
  }, [istAngemeldet, benutzer, pathname, router, status, statusFehler, statusLaedt]);

  if (!bereit && pathname !== '/login') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Prüft ob der Benutzer Zugriff auf eine Seite hat.
 */
export function hatZugriffAufSeite(
  benutzer: Benutzer | null | undefined,
  pfad: string,
  seitenFeatureFlags?: SeitenFeatureFlags | null,
  erpAdapterTyp?: string | null,
): boolean {
  return benutzerHatZugriffAufSeite(benutzer, pfad, seitenFeatureFlags, erpAdapterTyp);
}
